import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  isValidInputQuoteSize,
  isFinalValidInputQuoteSize,
  returnQuoteSizeWithMConvertedToThousands,
} from "@/lib/businessRules/quoteSizeRules";
import { useTheme } from "next-themes";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  updateFillQuantityInStore,
  getBlockNotionalFromStore,
  getTotalFillQuantityExcludingCurrent,
  isFillBlockFuture,
} from "./fillQuantityHelpers";

/**
 * FillQuantityCellEditor
 *
 * Cell editor for the fill quantity field in the Order Fills table.
 * Only available for RP role trades.
 * Updates the quantity value in the fills array within tradeDataStore.
 */
const FillQuantityCellEditor = forwardRef((params: any, ref) => {
  const { theme } = useTheme();
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  const fillId = params.data?.fillId;
  const blockId = params.data?.blockId;

  const [value, setValue] = useState(() => {
    const initialValue = params.value ?? "";
    return initialValue.toString();
  });

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow numbers and M/MM notation (e.g., "12", "12M", "12MM", "12.5m")
    if (!isValidInputQuoteSize(input)) return;

    setValue(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      params.stopEditing();
    } else if (e.key === "Escape") {
      params.stopEditing(true);
    }
  };

  const validateAndSave = () => {
    const esid = fullTradeData?.esid;

    if (value === "") {
      if (esid && fillId !== undefined && blockId !== undefined) {
        updateFillQuantityInStore(esid, fillId, blockId, undefined);
      }
      return undefined;
    }

    // Validate final input format (numbers with optional M/MM)
    if (!isFinalValidInputQuoteSize(value)) {
      // Invalid format - return original value
      toast("Invalid format. Fill quantity not updated.");
      return params.value;
    }

    // Convert M/MM notation to actual number (e.g., "12MM" -> 12000000, "12M" -> 12000)
    let newQuantity = returnQuoteSizeWithMConvertedToThousands(value);

    if (!esid || fillId === undefined || blockId === undefined) {
      return newQuantity;
    }

    // For futures, round to whole number (no decimals allowed for lots)
    if (isFillBlockFuture(esid, blockId)) {
      newQuantity = Math.round(newQuantity);
    }

    // Get block notional and calculate if new total would exceed 100%
    const blockNotional = getBlockNotionalFromStore(esid, blockId);
    const otherFillsTotal = getTotalFillQuantityExcludingCurrent(
      esid,
      blockId,
      fillId,
    );
    const newTotalFilled = otherFillsTotal + newQuantity;

    // Validate that total fill quantity doesn't exceed block notional (100%)
    if (blockNotional && newTotalFilled > blockNotional) {
      toast.error(
        "Unable to set fill quantity. Total fills would exceed 100% of the block size.",
      );
      return params.value;
    }

    // Update the Zustand store
    updateFillQuantityInStore(esid, fillId, blockId, newQuantity);

    return newQuantity;
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={`${
        theme === "light" ? "inputCellLight" : "inputCellDark"
      } ag-input ag-roundedInputStyle w-full justify-center !border-none text-center`}
      type="text"
      data-testid="fillQuantityCellEditor"
    />
  );
});

export default FillQuantityCellEditor;
